import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getToolOutput, useAgentChat } from "@cloudflare/ai-chat/react";
import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  groupPartByType,
  makeAssistantToolUI,
  useAuiState,
} from "@assistant-ui/react";
import { useAISDKRuntime } from "@assistant-ui/react-ai-sdk";
import { MarkdownTextPrimitive } from "@assistant-ui/react-markdown";
import remarkGfm from "remark-gfm";
import { useAgent } from "agents/react";
import { isToolUIPart, type UIMessage } from "ai";
import { API_URL } from "../lib/api-client";
import type {
  DispatchItem,
  DispatchProposal,
  DispatchWeek,
  OperatorNavigation,
  ScheduleBlock,
  Technician,
} from "../lib/dispatch";
import { formatDispatchDate, formatDispatchTime } from "../lib/dispatch";

const money = (cents: number | null | undefined) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    (cents ?? 0) / 100,
  );
const documentLabel = (
  kind: "Invoice" | "Estimate",
  displayNumber?: string | null,
) =>
  displayNumber?.trim()
    ? `${kind} #${displayNumber.trim()}`
    : `${kind} number unavailable`;

type DispatchOperatorState = {
  lastTurnIssue: {
    traceId: string;
    reason: string;
    kind: "empty_response" | "model_failure";
  } | null;
};

type JobOverview = {
  job: {
    id: string;
    jobNumber: number;
    status: string;
    description: string | null;
    priority: string | null;
    createdAt: string;
    customerId: string;
    customerName: string;
    customerEmail: string | null;
    addressLine1: string | null;
    city: string | null;
    state: string | null;
    postalCode: string | null;
  };
  appointments: Array<{
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    status: string;
    technicianNames: string | null;
  }>;
  invoices: Array<{
    id: string;
    number: number;
    displayNumber?: string | null;
    status: string;
    totalCents: number;
    balanceDueCents: number;
    dueDate: string | null;
    createdAt: string;
  }>;
  estimates: Array<{
    id: string;
    number: number;
    displayNumber?: string | null;
    title?: string | null;
    optionLabel?: string | null;
    status: string;
    totalCents: number;
    createdAt: string;
  }>;
};

type InvoiceResult = {
  id: string;
  number: number;
  displayNumber?: string | null;
  status: string;
  totalCents: number;
  balanceDueCents: number;
  dueDate: string | null;
  jobId: string;
  jobNumber: number;
  customerId: string;
  customerName: string;
};
type AttentionResult = {
  date: string;
  timezone: string;
  unassigned: DispatchItem[];
  conflicts: DispatchItem[];
};

type ToolOutput =
  | {
      kind: "navigation";
      view: OperatorNavigation["view"];
      date?: string;
      customerId?: string;
      jobId?: string;
    }
  | { kind: "proposal"; item: DispatchProposal }
  | {
      kind: "daily_board";
      board: {
        date: string;
        timezone: string;
        technicians: Technician[];
        blocks: ScheduleBlock[];
        appointments: DispatchItem[];
      };
    }
  | { kind: "weekly_summary"; week: DispatchWeek }
  | {
      kind: "availability";
      result: {
        date: string;
        timezone: string;
        items: Array<{
          technician: { id: string; name: string };
          windows: Array<{ startsAt: string; endsAt: string }>;
        }>;
      };
    }
  | {
      kind: "customer_results";
      query: string;
      items: Array<{
        id: string;
        name: string;
        email: string | null;
        address: string | null;
      }>;
    }
  | {
      kind: "job_results";
      query: string;
      items: Array<{
        id: string;
        jobNumber: number;
        description: string | null;
        status: string;
        customerId: string;
        customerName: string;
        address: string;
      }>;
    }
  | {
      kind: "money_workboard";
      result: {
        summary: {
          openEstimateCount: number;
          openEstimateValueCents: number;
          unpaidInvoiceValueCents: number;
          overdueValueCents: number;
          importReviewCount: number;
        };
        invoices: Array<{
          id: string;
          number: number;
          displayNumber?: string;
          totalCents: number;
          balanceDueCents: number;
          dueDate: string | null;
          jobId: string;
          jobNumber: number;
          customerName: string;
        }>;
        estimates: Array<{
          id: string;
          number: number;
          displayNumber?: string;
          title?: string | null;
          optionLabel?: string | null;
          totalCents: number;
          status: string;
          jobId: string;
          jobNumber: number;
          customerName: string;
        }>;
      };
    }
  | { kind: "job_overview"; result: JobOverview }
  | { kind: "invoice_results"; query: string; items: InvoiceResult[] }
  | { kind: "needs_attention"; result: AttentionResult };

function asToolOutput(value: unknown): ToolOutput | null {
  if (!value || typeof value !== "object" || !("kind" in value)) return null;
  const kind = (value as { kind?: unknown }).kind;
  if (
    kind !== "navigation" &&
    kind !== "proposal" &&
    kind !== "daily_board" &&
    kind !== "weekly_summary" &&
    kind !== "availability" &&
    kind !== "customer_results" &&
    kind !== "job_results" &&
    kind !== "money_workboard" &&
    kind !== "job_overview" &&
    kind !== "invoice_results" &&
    kind !== "needs_attention"
  )
    return null;
  return value as ToolOutput;
}

function outputs(message: UIMessage) {
  return message.parts.flatMap((part, index) => {
    if (!isToolUIPart(part)) return [];
    const output = asToolOutput(getToolOutput(part));
    return output ? [{ key: `${message.id}:${index}`, output }] : [];
  });
}

export function ProposalCard({
  proposal,
  timezone,
  busy = false,
  onApprove,
  onReject,
}: {
  proposal: DispatchProposal;
  timezone: string;
  busy?: boolean;
  onApprove?: (id: string) => void;
  onReject?: (id: string) => void;
}) {
  const display = proposal.payload.display;
  const label =
    proposal.changeType === "create_appointment"
      ? "Create appointment"
      : proposal.changeType === "move_appointment"
        ? "Move appointment"
        : "Reassign appointment";
  return (
    <article className="rounded-lg border border-[var(--line)] bg-[var(--wash)] p-3 text-left text-[var(--ink)]">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--accent)]">
            Dispatch proposal
          </p>
          <p className="mt-1 text-sm font-semibold">
            {label} · {display.jobId ? `Job #${display.jobNumber}` : `${display.ownerType ?? ''} appointment`}
          </p>
        </div>
        <span
          className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${proposal.status === "applied" ? "bg-[var(--wash)] text-[var(--ink)]" : proposal.status === "rejected" ? "bg-[var(--accent-wash)] text-[var(--accent)]" : "bg-[var(--wash)] text-[var(--ink)]"}`}
        >
          {proposal.status}
        </span>
      </div>
      <p className="mt-2 text-sm font-semibold">{display.customerName}</p>
      <p className="mt-1 text-xs text-[var(--ink-soft)]">{display.address}</p>
      <div className="mt-3 grid gap-2 rounded-lg bg-[var(--paper)] p-2 text-xs sm:grid-cols-2">
        {display.oldStart && (
          <div>
            <span className="text-[var(--ink-faint)]">Current</span>
            <p className="mt-0.5 font-semibold">
              {formatDispatchDate(display.oldStart, timezone, {
                weekday: "short",
              })}{" "}
              · {formatDispatchTime(display.oldStart, timezone)}–
              {formatDispatchTime(display.oldEnd ?? display.oldStart, timezone)}
            </p>
          </div>
        )}
        <div>
          <span className="text-[var(--ink-faint)]">Proposed</span>
          <p className="mt-0.5 font-semibold">
            {formatDispatchDate(display.newStart, timezone, {
              weekday: "short",
            })}{" "}
            · {formatDispatchTime(display.newStart, timezone)}–
            {formatDispatchTime(display.newEnd, timezone)}
          </p>
        </div>
        <div className="sm:col-span-2">
          <span className="text-[var(--ink-faint)]">Technician</span>
          <p className="mt-0.5 font-semibold">
            {display.technicianNames.join(", ") || "Unassigned"}
          </p>
        </div>
      </div>
      {display.conflicts.length > 0 ? (
        <div className="mt-2 rounded-lg bg-[var(--accent-wash)] px-3 py-2 text-xs text-[var(--accent)]">
          <p className="font-semibold">Resolve before approval</p>
          {display.conflicts.map((conflict) => (
            <p key={conflict} className="mt-1">
              • {conflict}
            </p>
          ))}
        </div>
      ) : (
        <p className="mt-2 text-xs font-semibold text-[var(--ink)]">
          No availability conflicts found.
        </p>
      )}
      <p className="mt-2 text-[11px] text-[var(--ink-faint)]">
        Requested by {proposal.requestedByName || "WorkView operator"} ·{" "}
        {new Date(proposal.createdAt).toLocaleString()}
      </p>
      {proposal.status === "pending" && onApprove && onReject && (
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            disabled={busy || display.conflicts.length > 0}
            onClick={() => onApprove(proposal.id)}
            className="rounded-lg bg-[var(--ink)] px-3 py-2 text-xs font-semibold text-[var(--paper)] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Approve & apply
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onReject(proposal.id)}
            className="rounded-lg border border-[var(--line)] bg-[var(--paper)] px-3 py-2 text-xs font-semibold text-[var(--ink-soft)] disabled:opacity-40"
          >
            Reject
          </button>
        </div>
      )}
    </article>
  );
}

export function shouldRenderAssistantText(hasTool: boolean) {
  return !hasTool;
}

export function freshTurnMessageIds(
  messages: readonly UIMessage[],
  turnStart: number,
) {
  return new Set(messages.slice(turnStart).map((message) => message.id));
}

function localMinutes(value: string, timezone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date(value));
  const record = Object.fromEntries(
    parts.map((part) => [part.type, part.value]),
  );
  return Number(record.hour) * 60 + Number(record.minute);
}

export function DailyBoardCard({
  board,
  onOpenBoard,
  onOpenJob,
}: {
  board: Extract<ToolOutput, { kind: "daily_board" }>["board"];
  onOpenBoard: () => void;
  onOpenJob: (jobId: string) => void;
}) {
  const start = 8 * 60;
  const span = 10 * 60;
  const position = (value: string) =>
    Math.max(
      0,
      Math.min(
        100,
        ((localMinutes(value, board.timezone) - start) / span) * 100,
      ),
    );
  const assigned = (technicianId: string) =>
    board.appointments.filter((item) =>
      item.technicianIds.includes(technicianId),
    );
  const unassigned = board.appointments.filter(
    (item) => item.technicianIds.length === 0,
  );
  const hours = Array.from({ length: 6 }, (_, index) => start + index * 120);
  return (
    <section className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-white bg-[var(--accent)]">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[.12em] text-white/80">
            Native WorkView board
          </p>
          <p className="mt-0.5 text-sm font-semibold">
            {formatDispatchDate(`${board.date}T12:00:00Z`, board.timezone, {
              weekday: "long",
              year: "numeric",
            })}{" "}
            · {board.appointments.length} jobs
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenBoard}
          className="rounded-lg bg-[var(--paper)]/15 px-3 py-2 text-xs font-semibold hover:bg-[var(--paper)]/25"
        >
          Open full board
        </button>
      </header>
      <div className="overflow-x-auto">
        <div className="min-w-[680px]">
          <div className="grid grid-cols-[166px_1fr] border-b border-[var(--line)]">
            <div className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-[var(--ink-faint)]">
              Technician
            </div>
            <div className="relative h-10 bg-[var(--wash)]">
              {hours.map((hour) => (
                <span
                  key={hour}
                  className="absolute top-3 -translate-x-1/2 text-[11px] font-medium text-[var(--ink-faint)]"
                  style={{ left: `${((hour - start) / span) * 100}%` }}
                >
                  {hour === 12 * 60
                    ? "12p"
                    : hour < 12 * 60
                      ? `${hour / 60}a`
                      : `${hour / 60 - 12}p`}
                </span>
              ))}
            </div>
          </div>
          {board.technicians.map((technician) => {
            const techBlocks = board.blocks.filter(
              (block) => block.technicianId === technician.id,
            );
            const jobs = assigned(technician.id);
            return (
              <div
                key={technician.id}
                className="grid grid-cols-[166px_1fr] border-b border-[var(--line)] last:border-0"
              >
                <div className="px-3 py-3">
                  <p className="text-sm font-semibold text-[var(--ink)]">
                    {technician.name}
                  </p>
                  <p className="mt-1 text-[11px] font-semibold text-[var(--ink)]">
                    {techBlocks
                      .filter(
                        (block) =>
                          block.kind === "working" || block.kind === "on_call",
                      )
                      .map(
                        (block) =>
                          `${formatDispatchTime(block.startsAt, board.timezone)}–${formatDispatchTime(block.endsAt, board.timezone)}`,
                      )
                      .join(" · ") || "No working hours"}
                  </p>
                </div>
                <div
                  className="relative min-h-[78px] bg-[linear-gradient(to_right,rgba(148,163,184,.18)_1px,transparent_1px)]"
                  style={{ backgroundSize: "20% 100%" }}
                >
                  {techBlocks.map((block) => {
                    const left = position(block.startsAt);
                    const right = position(block.endsAt);
                    return (
                      <div
                        key={block.id}
                        className={`absolute inset-y-2 rounded-lg ${block.kind === "working" || block.kind === "on_call" ? "bg-[var(--wash)]" : "bg-[var(--accent-wash)]"}`}
                        style={{
                          left: `${left}%`,
                          width: `${Math.max(1, right - left)}%`,
                        }}
                      />
                    );
                  })}
                  {jobs.map((job, index) => {
                    const left = position(job.scheduledStart);
                    const right = position(job.scheduledEnd);
                    return (
                      <button
                        type="button"
                        key={job.id}
                        onClick={() => job.jobId ? onOpenJob(job.jobId) : onOpenBoard()}
                        className={`absolute z-10 overflow-hidden rounded-lg px-2 py-1.5 text-left text-[11px] font-semibold text-[var(--paper)] ${job.conflicts.length ? "bg-[var(--accent-wash)]" : "bg-[var(--ink)]"}`}
                        style={{
                          left: `${left}%`,
                          width: `${Math.max(12, right - left)}%`,
                          top: `${9 + index * 33}px`,
                        }}
                      >
                        <span className="block truncate">
                          {job.jobId ? `#${job.jobNumber}` : `${job.ownerType} appointment`} {job.customerName}
                        </span>
                      </button>
                    );
                  })}
                  {!jobs.length && (
                    <p className="absolute inset-0 grid place-items-center text-xs text-[var(--ink-faint)]">
                      Open lane
                    </p>
                  )}
                </div>
              </div>
            );
          })}
          {unassigned.length > 0 && (
            <div className="border-t border-[var(--line)] bg-[var(--wash)] px-4 py-3">
              <p className="text-xs font-semibold text-[var(--ink)]">
                Unassigned · {unassigned.length}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {unassigned.map((job) => (
                  <button
                    key={job.id}
                    type="button"
                    onClick={() => job.jobId ? onOpenJob(job.jobId) : onOpenBoard()}
                    className="rounded-full bg-[var(--paper)] px-2.5 py-1.5 text-xs font-semibold text-[var(--ink-soft)]"
                  >
                    {job.jobId ? `#${job.jobNumber}` : `${job.ownerType} appointment`} {job.customerName}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function WeeklySummaryCard({
  week,
  onOpenDay,
}: {
  week: DispatchWeek;
  onOpenDay: (date: string) => void;
}) {
  const total = week.days.reduce((sum, day) => sum + day.total, 0);
  const unassigned = week.days.reduce((sum, day) => sum + day.unassigned, 0);
  const conflicts = week.days.reduce((sum, day) => sum + day.conflicts, 0);
  return (
    <section className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-white bg-[var(--accent)]">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[.12em] text-white/80">
            Native WorkView schedule
          </p>
          <p className="mt-0.5 text-sm font-semibold">
            Week of{" "}
            {formatDispatchDate(`${week.weekStart}T12:00:00Z`, week.timezone, {
              month: "short",
              day: "numeric",
            })}
            –
            {formatDispatchDate(`${week.weekEnd}T12:00:00Z`, week.timezone, {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </p>
        </div>
        <span className="rounded-lg bg-[var(--paper)]/15 px-3 py-2 text-xs font-semibold">
          {total} jobs
        </span>
      </header>
      <div className="grid gap-2 p-3 sm:grid-cols-2 lg:grid-cols-4">
        {week.days.map((day) => (
          <button
            key={day.date}
            type="button"
            onClick={() => onOpenDay(day.date)}
            className="rounded-lg border border-[var(--line)] bg-[var(--wash)] p-3 text-left transition hover:border-[var(--line)] hover:bg-[var(--wash)]"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-[var(--ink)]">
                {formatDispatchDate(`${day.date}T12:00:00Z`, week.timezone, {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </p>
              <span className="text-lg font-semibold text-[var(--accent)]">
                {day.total}
              </span>
            </div>
            <p className="mt-2 text-[11px] text-[var(--ink-soft)]">
              {day.workingTechnicians} working tech
              {day.workingTechnicians === 1 ? "" : "s"}
            </p>
            <p
              className={`mt-1 text-[11px] font-semibold ${day.unassigned ? "text-[var(--ink)]" : "text-[var(--ink)]"}`}
            >
              {day.unassigned ? `${day.unassigned} unassigned` : "All assigned"}
              {day.conflicts
                ? ` · ${day.conflicts} conflict${day.conflicts === 1 ? "" : "s"}`
                : ""}
            </p>
          </button>
        ))}
      </div>
      <footer className="flex flex-wrap gap-x-5 gap-y-1 border-t border-[var(--line)] px-4 py-3 text-xs font-semibold text-[var(--ink-soft)]">
        <span>{total} jobs this week</span>
        <span>{unassigned} unassigned</span>
        <span
          className={conflicts ? "text-[var(--accent)]" : "text-[var(--ink)]"}
        >
          {conflicts ? `${conflicts} conflicts` : "No conflicts"}
        </span>
      </footer>
    </section>
  );
}

export function AvailabilityCard({
  result,
}: {
  result: Extract<ToolOutput, { kind: "availability" }>["result"];
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-white bg-[var(--team)]">
        <p className="text-xs font-semibold uppercase tracking-[.12em] text-white/80">
          Technician availability
        </p>
        <p className="mt-0.5 text-sm font-semibold">
          Open windows ·{" "}
          {formatDispatchDate(`${result.date}T12:00:00Z`, result.timezone, {
            weekday: "long",
            month: "short",
            day: "numeric",
          })}
        </p>
      </header>
      <div className="divide-y divide-[var(--line)]">
        {result.items.map((item) => (
          <div
            key={item.technician.id}
            className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
          >
            <p className="text-sm font-semibold text-[var(--ink)]">
              {item.technician.name}
            </p>
            <div className="flex flex-wrap justify-end gap-1.5">
              {item.windows.map((window) => (
                <span
                  key={window.startsAt}
                  className="rounded-full bg-[var(--wash)] px-2.5 py-1 text-xs font-semibold text-[var(--ink)]"
                >
                  {formatDispatchTime(window.startsAt, result.timezone)}–
                  {formatDispatchTime(window.endsAt, result.timezone)}
                </span>
              ))}
            </div>
          </div>
        ))}
        {!result.items.length && (
          <p className="px-4 py-6 text-sm text-[var(--ink-soft)]">
            No open windows matched that request.
          </p>
        )}
      </div>
    </section>
  );
}

export function SearchResultsCard({
  kind,
  query,
  items,
  onNavigate,
}: {
  kind: "customer_results" | "job_results";
  query: string;
  items: Array<{
    id: string;
    name?: string;
    email?: string | null;
    address: string | null;
    jobNumber?: number;
    customerName?: string;
  }>;
  onNavigate: (navigation: OperatorNavigation) => void;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-white bg-[var(--accent)]">
        <p className="text-xs font-semibold uppercase tracking-[.12em] text-white/80">
          {kind === "customer_results" ? "Customer results" : "Job results"}
        </p>
        <p className="mt-0.5 text-sm font-semibold text-white/80">
          Matches for “{query}”
        </p>
      </header>
      <div className="divide-y divide-[var(--line)]">
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() =>
              onNavigate(
                kind === "customer_results"
                  ? { view: "customer", customerId: item.id }
                  : { view: "job", jobId: item.id },
              )
            }
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition hover:bg-[var(--wash)]"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[var(--ink)]">
                {kind === "customer_results"
                  ? item.name
                  : `${item.customerName} · Job #${item.jobNumber}`}
              </p>
              <p className="mt-1 truncate text-xs text-[var(--ink-soft)]">
                {kind === "customer_results"
                  ? item.address || item.email || "No contact details"
                  : item.address}
              </p>
            </div>
            <span className="shrink-0 text-xs font-semibold text-[var(--accent)]">
              Open →
            </span>
          </button>
        ))}
        {!items.length && (
          <p className="px-4 py-6 text-sm text-[var(--ink-soft)]">
            No matches found.
          </p>
        )}
      </div>
    </section>
  );
}

export function MoneyWorkboardCard({
  result,
  onOpenJob,
}: {
  result: Extract<ToolOutput, { kind: "money_workboard" }>["result"];
  onOpenJob: (jobId: string) => void;
}) {
  const summary = result.summary;
  return (
    <section className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-white bg-[var(--st-complete)]">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[.12em] text-white/80">
            Money work board
          </p>
          <p className="mt-0.5 text-sm font-semibold">
            What needs office attention
          </p>
        </div>
        <span className="rounded-lg bg-[var(--paper)]/15 px-3 py-2 text-xs font-semibold">
          {result.invoices.length + result.estimates.length} items
        </span>
      </header>
      <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-4">
        <div className="rounded-lg bg-[var(--wash)] p-3">
          <p className="text-[11px] font-semibold text-[var(--ink)]">
            Money owed
          </p>
          <p className="mt-1 text-lg font-semibold text-[var(--ink)]">
            {money(summary.unpaidInvoiceValueCents)}
          </p>
        </div>
        <div className="rounded-lg bg-[var(--accent-wash)] p-3">
          <p className="text-[11px] font-semibold text-[var(--accent)]">
            Overdue
          </p>
          <p className="mt-1 text-lg font-semibold text-[var(--ink)]">
            {money(summary.overdueValueCents)}
          </p>
        </div>
        <div className="rounded-lg bg-[var(--wash)] p-3">
          <p className="text-[11px] font-semibold text-[var(--accent)]">
            Estimates
          </p>
          <p className="mt-1 text-lg font-semibold text-[var(--ink)]">
            {summary.openEstimateCount}
          </p>
        </div>
        <div className="rounded-lg bg-[var(--wash)] p-3">
          <p className="text-[11px] font-semibold text-[var(--ink-soft)]">
            Import review
          </p>
          <p className="mt-1 text-lg font-semibold text-[var(--ink)]">
            {summary.importReviewCount}
          </p>
        </div>
      </div>
      <div className="divide-y divide-[var(--line)]">
        {result.invoices.slice(0, 5).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onOpenJob(item.jobId)}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[var(--wash)]"
          >
            <div>
              <p className="text-sm font-semibold text-[var(--ink)]">
                {item.customerName}
              </p>
              <p className="mt-1 text-xs text-[var(--ink-soft)]">
                {documentLabel("Invoice", item.displayNumber)} · Job #
                {item.jobNumber}
              </p>
            </div>
            <span className="text-sm font-semibold text-[var(--ink)]">
              {money(item.balanceDueCents)} due
            </span>
          </button>
        ))}
        {!result.invoices.length && (
          <p className="px-4 py-5 text-sm text-[var(--ink-soft)]">
            No open invoice balances need attention.
          </p>
        )}
      </div>
    </section>
  );
}

export function JobOverviewCard({
  result,
  timezone,
  onOpenJob,
  onOpenCustomer,
}: {
  result: JobOverview;
  timezone: string;
  onOpenJob: (jobId: string) => void;
  onOpenCustomer: (customerId: string) => void;
}) {
  const { job, appointments, invoices, estimates } = result;
  const address = [job.addressLine1, job.city, job.state, job.postalCode]
    .filter(Boolean)
    .join(", ");
  return (
    <section className="overflow-hidden rounded-xl border border-violet-200 bg-[var(--paper)] text-[var(--ink)]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-white bg-[var(--accent)]">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[.12em] text-white/80">
            Job overview
          </p>
          <p className="mt-0.5 text-sm font-semibold">
            Job #{job.jobNumber} · {job.status}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onOpenJob(job.id)}
          className="rounded-lg bg-[var(--paper)]/15 px-3 py-2 text-xs font-semibold hover:bg-[var(--paper)]/25"
        >
          Open job
        </button>
      </header>
      <div className="grid gap-4 p-4 lg:grid-cols-[1.1fr_.9fr]">
        <div>
          <button
            type="button"
            onClick={() => onOpenCustomer(job.customerId)}
            className="text-left"
          >
            <p className="text-base font-semibold text-[var(--ink)] hover:text-violet-700">
              {job.customerName}
            </p>
            <p className="mt-1 text-xs text-[var(--ink-soft)]">
              {address || job.customerEmail || "No contact details"}
            </p>
          </button>
          {job.description && (
            <p className="mt-3 text-sm leading-6 text-[var(--ink-soft)]">
              {job.description}
            </p>
          )}
          <div className="mt-4">
            <p className="text-[11px] font-semibold uppercase tracking-[.12em] text-[var(--ink-faint)]">
              Appointments
            </p>
            <div className="mt-2 space-y-2">
              {appointments.slice(0, 4).map((item) => (
                <div
                  key={item.id}
                  className="rounded-lg bg-[var(--wash)] px-3 py-2"
                >
                  <p className="text-xs font-semibold text-[var(--ink)]">
                    {formatDispatchDate(item.scheduledStart, timezone, {
                      weekday: "short",
                      month: "short",
                      day: "numeric",
                    })}{" "}
                    · {formatDispatchTime(item.scheduledStart, timezone)}–
                    {formatDispatchTime(item.scheduledEnd, timezone)}
                  </p>
                  <p className="mt-1 text-[11px] text-[var(--ink-soft)]">
                    {item.technicianNames || "Unassigned"} · {item.status}
                  </p>
                </div>
              ))}
              {!appointments.length && (
                <p className="rounded-lg bg-[var(--wash)] px-3 py-3 text-xs text-[var(--ink-soft)]">
                  No appointments on this job.
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[.12em] text-[var(--ink-faint)]">
              Invoices
            </p>
            <div className="mt-2 space-y-2">
              {invoices.slice(0, 4).map((item) => (
                <div
                  key={item.id}
                  className="rounded-lg border border-[var(--line)] bg-[var(--wash)] px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold text-[var(--ink)]">
                      {documentLabel("Invoice", item.displayNumber)}
                    </p>
                    <span className="text-xs font-semibold text-[var(--ink)]">
                      {money(item.balanceDueCents)} due
                    </span>
                  </div>
                  <p className="mt-1 text-[11px] text-[var(--ink-soft)]">
                    {item.status} · {money(item.totalCents)}
                    {item.dueDate ? ` · due ${item.dueDate}` : ""}
                  </p>
                </div>
              ))}
              {!invoices.length && (
                <p className="rounded-lg bg-[var(--wash)] px-3 py-3 text-xs text-[var(--ink-soft)]">
                  No invoice summaries available.
                </p>
              )}
            </div>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[.12em] text-[var(--ink-faint)]">
              Estimates
            </p>
            <div className="mt-2 space-y-2">
              {estimates.slice(0, 4).map((item) => (
                <div
                  key={item.id}
                  className="rounded-lg bg-[var(--wash)] px-3 py-2"
                >
                  <p className="text-xs font-semibold text-[var(--ink)]">
                    {documentLabel("Estimate", item.displayNumber)}
                    {item.title || item.optionLabel
                      ? ` · ${item.title || item.optionLabel}`
                      : ""}{" "}
                    · {money(item.totalCents)}
                  </p>
                  <p className="mt-1 text-[11px] text-[var(--ink-soft)]">
                    {item.status}
                  </p>
                </div>
              ))}
              {!estimates.length && (
                <p className="rounded-lg bg-[var(--wash)] px-3 py-3 text-xs text-[var(--ink-soft)]">
                  No estimate summaries available.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function InvoiceFocusCard({
  query,
  items,
  onOpenJob,
  onOpenCustomer,
}: {
  query: string;
  items: InvoiceResult[];
  onOpenJob: (jobId: string) => void;
  onOpenCustomer: (customerId: string) => void;
}) {
  return (
    <section className="overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--paper)] text-[var(--ink)]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-white bg-[var(--st-complete)]">
        <p className="text-xs font-semibold uppercase tracking-[.12em] text-white/80">
          Invoice focus
        </p>
        <p className="mt-0.5 text-sm font-semibold">Matches for “{query}”</p>
      </header>
      <div className="divide-y divide-[var(--line)]">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
          >
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[var(--ink)]">
                {documentLabel("Invoice", item.displayNumber)} ·{" "}
                {money(item.balanceDueCents)} due
              </p>
              <p className="mt-1 text-xs text-[var(--ink-soft)]">
                {item.status} · {money(item.totalCents)}
                {item.dueDate ? ` · due ${item.dueDate}` : ""}
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={() => onOpenCustomer(item.customerId)}
                className="text-xs font-semibold text-[var(--ink-soft)] hover:text-[var(--accent)]"
              >
                {item.customerName}
              </button>
              <button
                type="button"
                onClick={() => onOpenJob(item.jobId)}
                className="rounded-full bg-[var(--wash)] px-2.5 py-1.5 text-xs font-semibold text-[var(--ink)] hover:bg-[var(--wash)]"
              >
                Job #{item.jobNumber} →
              </button>
            </div>
          </div>
        ))}
        {!items.length && (
          <p className="px-4 py-6 text-sm text-[var(--ink-soft)]">
            No invoices matched that request.
          </p>
        )}
      </div>
    </section>
  );
}

export function NeedsAttentionCard({
  result,
  onOpenBoard,
  onOpenJob,
}: {
  result: AttentionResult;
  onOpenBoard: () => void;
  onOpenJob: (jobId: string) => void;
}) {
  const section = (
    title: string,
    items: DispatchItem[],
    tone: "amber" | "rose",
  ) => (
    <div>
      <div
        className={`flex items-center justify-between px-4 py-2 ${tone === "rose" ? "bg-[var(--accent-wash)]" : "bg-[var(--wash)]"}`}
      >
        <p
          className={`text-xs font-semibold ${tone === "rose" ? "text-[var(--accent)]" : "text-[var(--ink)]"}`}
        >
          {title}
        </p>
        <span
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${tone === "rose" ? "bg-[var(--accent-wash)] text-[var(--accent)]" : "bg-[var(--wash)] text-[var(--ink)]"}`}
        >
          {items.length}
        </span>
      </div>
      <div className="divide-y divide-[var(--line)]">
        {items.map((item) => (
          <button
            key={`${title}-${item.id}`}
            type="button"
            onClick={() => item.jobId ? onOpenJob(item.jobId) : onOpenBoard()}
            className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-[var(--wash)]"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[var(--ink)]">
                {item.jobId ? `#${item.jobNumber}` : `${item.ownerType} appointment`} · {item.customerName}
              </p>
              <p className="mt-1 truncate text-xs text-[var(--ink-soft)]">
                {formatDispatchTime(item.scheduledStart, result.timezone)} ·{" "}
                {item.address}
              </p>
              {tone === "rose" && (
                <p className="mt-1 text-[11px] font-semibold text-[var(--accent)]">
                  {item.conflicts.join(" · ")}
                </p>
              )}
            </div>
            <span className="shrink-0 text-xs font-semibold text-[var(--accent)]">
              Open →
            </span>
          </button>
        ))}
        {!items.length && (
          <p className="px-4 py-4 text-xs text-[var(--ink-soft)]">
            Nothing needs attention here.
          </p>
        )}
      </div>
    </div>
  );
  return (
    <section className="overflow-hidden rounded-xl border border-[var(--accent)] bg-[var(--paper)] text-[var(--ink)]">
      <header className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-white bg-[var(--st-conflict)]">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[.12em] text-white/80">
            Dispatch attention
          </p>
          <p className="mt-0.5 text-sm font-semibold">
            Exceptions for{" "}
            {formatDispatchDate(`${result.date}T12:00:00Z`, result.timezone, {
              weekday: "long",
              month: "short",
              day: "numeric",
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenBoard}
          className="rounded-lg bg-[var(--paper)]/15 px-3 py-2 text-xs font-semibold hover:bg-[var(--paper)]/25"
        >
          Open board
        </button>
      </header>
      {section("Unassigned appointments", result.unassigned, "amber")}
      {section("Scheduling conflicts", result.conflicts, "rose")}
    </section>
  );
}

type LandingActions = {
  timezone: string;
  proposals: DispatchProposal[];
  proposalBusy: string | null;
  onNavigate: (navigation: OperatorNavigation) => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
};

const LandingActionsContext = createContext<LandingActions | null>(null);

function useLandingActions() {
  const actions = useContext(LandingActionsContext);
  if (!actions)
    throw new Error(
      "Agent tool widgets must be rendered inside the Agent workspace.",
    );
  return actions;
}

function LandingDayTool({ result }: { result: unknown }) {
  const actions = useLandingActions();
  const output = asToolOutput(result);
  if (output?.kind !== "daily_board") return null;
  return (
    <DailyBoardCard
      board={output.board}
      onOpenBoard={() =>
        actions.onNavigate({ view: "dispatch", date: output.board.date })
      }
      onOpenJob={(jobId) => actions.onNavigate({ view: "job", jobId })}
    />
  );
}

function LandingWeekTool({ result }: { result: unknown }) {
  const actions = useLandingActions();
  const output = asToolOutput(result);
  if (output?.kind !== "weekly_summary") return null;
  return (
    <WeeklySummaryCard
      week={output.week}
      onOpenDay={(date) => actions.onNavigate({ view: "dispatch", date })}
    />
  );
}

function LandingAvailabilityTool({ result }: { result: unknown }) {
  const output = asToolOutput(result);
  return output?.kind === "availability" ? (
    <AvailabilityCard result={output.result} />
  ) : null;
}

function LandingResultsTool({
  result,
  kind,
}: {
  result: unknown;
  kind: "customer_results" | "job_results";
}) {
  const actions = useLandingActions();
  const output = asToolOutput(result);
  if (output?.kind !== kind) return null;
  return (
    <SearchResultsCard
      kind={kind}
      query={output.query}
      items={output.items}
      onNavigate={actions.onNavigate}
    />
  );
}

function LandingMoneyTool({ result }: { result: unknown }) {
  const actions = useLandingActions();
  const output = asToolOutput(result);
  return output?.kind === "money_workboard" ? (
    <MoneyWorkboardCard
      result={output.result}
      onOpenJob={(jobId) => actions.onNavigate({ view: "job", jobId })}
    />
  ) : null;
}

function LandingJobOverviewTool({ result }: { result: unknown }) {
  const actions = useLandingActions();
  const output = asToolOutput(result);
  return output?.kind === "job_overview" ? (
    <JobOverviewCard
      result={output.result}
      timezone={actions.timezone}
      onOpenJob={(jobId) => actions.onNavigate({ view: "job", jobId })}
      onOpenCustomer={(customerId) =>
        actions.onNavigate({ view: "customer", customerId })
      }
    />
  ) : null;
}

function LandingInvoiceTool({ result }: { result: unknown }) {
  const actions = useLandingActions();
  const output = asToolOutput(result);
  return output?.kind === "invoice_results" ? (
    <InvoiceFocusCard
      query={output.query}
      items={output.items}
      onOpenJob={(jobId) => actions.onNavigate({ view: "job", jobId })}
      onOpenCustomer={(customerId) =>
        actions.onNavigate({ view: "customer", customerId })
      }
    />
  ) : null;
}

function LandingAttentionTool({ result }: { result: unknown }) {
  const actions = useLandingActions();
  const output = asToolOutput(result);
  return output?.kind === "needs_attention" ? (
    <NeedsAttentionCard
      result={output.result}
      onOpenBoard={() =>
        actions.onNavigate({ view: "dispatch", date: output.result.date })
      }
      onOpenJob={(jobId) => actions.onNavigate({ view: "job", jobId })}
    />
  ) : null;
}

function LandingProposalTool({ result }: { result: unknown }) {
  const actions = useLandingActions();
  const output = asToolOutput(result);
  if (output?.kind !== "proposal") return null;
  const proposal =
    actions.proposals.find((item) => item.id === output.item.id) ?? output.item;
  return (
    <ProposalCard
      proposal={proposal}
      timezone={actions.timezone}
      busy={actions.proposalBusy === proposal.id}
      onApprove={actions.onApprove}
      onReject={actions.onReject}
    />
  );
}

const DayToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "getDaySchedule",
  display: "standalone",
  render: ({ result }) => <LandingDayTool result={result} />,
});
const WeekToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "getWeekSchedule",
  display: "standalone",
  render: ({ result }) => <LandingWeekTool result={result} />,
});
const AvailabilityToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "getTechnicianAvailability",
  display: "standalone",
  render: ({ result }) => <LandingAvailabilityTool result={result} />,
});
const MoneyToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "getMoneyWorkboard",
  display: "standalone",
  render: ({ result }) => <LandingMoneyTool result={result} />,
});
const CustomerToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "findCustomers",
  display: "standalone",
  render: ({ result }) => (
    <LandingResultsTool result={result} kind="customer_results" />
  ),
});
const JobToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "findJobs",
  display: "standalone",
  render: ({ result }) => (
    <LandingResultsTool result={result} kind="job_results" />
  ),
});
const JobOverviewToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "getJobOverview",
  display: "standalone",
  render: ({ result }) => <LandingJobOverviewTool result={result} />,
});
const InvoiceToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "findInvoices",
  display: "standalone",
  render: ({ result }) => <LandingInvoiceTool result={result} />,
});
const AttentionToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "getNeedsAttention",
  display: "standalone",
  render: ({ result }) => <LandingAttentionTool result={result} />,
});
const CreateProposalToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "draftCreateAppointment",
  display: "standalone",
  render: ({ result }) => <LandingProposalTool result={result} />,
});
const MoveProposalToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "draftMoveAppointment",
  display: "standalone",
  render: ({ result }) => <LandingProposalTool result={result} />,
});
const ReassignProposalToolUI = makeAssistantToolUI<unknown, unknown>({
  toolName: "draftReassignAppointment",
  display: "standalone",
  render: ({ result }) => <LandingProposalTool result={result} />,
});

function AgentWorkspaceToolWidgets() {
  return (
    <>
      <DayToolUI />
      <WeekToolUI />
      <AvailabilityToolUI />
      <MoneyToolUI />
      <CustomerToolUI />
      <JobToolUI />
      <JobOverviewToolUI />
      <InvoiceToolUI />
      <AttentionToolUI />
      <CreateProposalToolUI />
      <MoveProposalToolUI />
      <ReassignProposalToolUI />
    </>
  );
}

function HiddenTool() {
  return null;
}

const VisibleMessageIds = createContext<Set<string> | null>(null);

function AssistantMessage() {
  const message = useAuiState((state) => state.message);
  const visibleMessageIds = useContext(VisibleMessageIds);
  if (visibleMessageIds && !visibleMessageIds.has(message.id)) return null;
  const hasText = message.content.some(
    (part) => part.type === "text" && part.text.trim(),
  );
  const hasTool = message.content.some((part) => part.type === "tool-call");
  if (message.role === "assistant" && !hasText && !hasTool) return null;
  return (
    <MessagePrimitive.Root
      className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}
    >
      <div
        className={
          message.role === "user"
            ? "max-w-[88%] rounded-2xl rounded-br-md bg-[var(--wash)] px-4 py-3 text-sm text-[var(--ink)]"
            : "w-full max-w-full text-sm leading-6 text-[var(--ink)]"
        }
      >
        {message.role === "assistant" ? (
          <MessagePrimitive.GroupedParts
            groupBy={groupPartByType({
              reasoning: ["group-chainOfThought", "group-reasoning"],
              "tool-call": ["group-chainOfThought", "group-tool"],
              "standalone-tool-call": [],
            })}
          >
            {({ part }) =>
              part.type === "text" ? (
                shouldRenderAssistantText(hasTool) ? (
                  <MarkdownTextPrimitive
                    remarkPlugins={[remarkGfm]}
                    className="aui-md"
                    defer
                  />
                ) : null
              ) : part.type === "tool-call" ? (
                (part.toolUI ?? <HiddenTool />)
              ) : null
            }
          </MessagePrimitive.GroupedParts>
        ) : (
          <MessagePrimitive.Content
            components={{ tools: { Fallback: HiddenTool } }}
          />
        )}
      </div>
    </MessagePrimitive.Root>
  );
}

/**
 * A line, not a box. The nine-cell square read as a loading tile parked on the
 * thread — three dots keeping time beside the words say the same thing without
 * drawing a second object on the screen.
 */
function Thinking({ label }: { label: string }) {
  return (
    <div className="mb-3 flex items-center gap-2.5 px-2">
      <span aria-hidden="true" className="flex items-center gap-1">
        {[0, 1, 2].map((index) => (
          <span
            key={index}
            className="wv-pixel size-1.5 rounded-full bg-[var(--accent)]"
            style={{ animationDelay: `${index * 160}ms` }}
          />
        ))}
      </span>
      <span className="wv-shimmer text-sm font-medium">{label}</span>
    </div>
  );
}

function AssistantLanding({
  chat,
  busy,
  error,
  actions,
  userName,
}: {
  chat: ReturnType<typeof useAgentChat>;
  busy: boolean;
  error: boolean;
  actions: LandingActions;
  userName: string;
}) {
  const runtime = useAISDKRuntime(chat);
  const [turnStart, setTurnStart] = useState(chat.messages.length);
  const visibleMessageIds = useMemo(
    () => freshTurnMessageIds(chat.messages, turnStart),
    [chat.messages, turnStart],
  );
  const suggestions = [
    "Show today’s board",
    "Show this week’s schedule",
    "Find a customer",
    "Check technician availability",
  ];
  const sendSuggestion = (suggestion: string) => {
    setTurnStart(chat.messages.length);
    void chat.sendMessage({ text: suggestion });
  };
  const empty = visibleMessageIds.size === 0;
  const hour = new Date().getHours();
  const partOfDay =
    hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const first = (userName || "").trim().split(/\s+/)[0];
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <LandingActionsContext.Provider value={actions}>
        <AgentWorkspaceToolWidgets />
        {/*
         * Empty, the greeting and the composer are one block sitting in the
         * middle of the screen. They used to be pinned to opposite ends of a
         * viewport that stretched whether there was a conversation in it or
         * not, which left a window of white between them.
         */}
        <ThreadPrimitive.Root
          className={`relative flex w-full flex-col ${empty ? "min-h-[calc(100vh-230px)] justify-center" : "min-h-[calc(100vh-300px)]"}`}
        >
          {empty ? (
            <h1 className="mb-7 text-center text-[28px] font-normal tracking-tight text-[var(--ink)]">
              {first ? (
                <>
                  {partOfDay},{" "}
                  <span className="text-[var(--accent)]">{first}</span>
                </>
              ) : (
                partOfDay
              )}
            </h1>
          ) : (
            <ThreadPrimitive.Viewport
              className="min-h-[340px] flex-1 space-y-5 overflow-y-auto px-1 pb-8"
              autoScroll
            >
              <VisibleMessageIds.Provider value={visibleMessageIds}>
                <ThreadPrimitive.Messages>
                  {() => <AssistantMessage />}
                </ThreadPrimitive.Messages>
              </VisibleMessageIds.Provider>
            </ThreadPrimitive.Viewport>
          )}
          <div
            className={
              empty
                ? ""
                : "sticky bottom-0 z-10 bg-gradient-to-t from-white via-white to-white/70 pt-4"
            }
          >
            {busy && <Thinking label="Checking the board" />}
            {error && (
              <p className="mb-2 rounded-lg border border-[var(--accent)] bg-[var(--accent-wash)] px-3 py-2 text-xs text-[var(--accent)]">
                The Agent connection failed. Your board is unchanged; refresh or
                try again.
              </p>
            )}
            <ComposerPrimitive.Root
              onSubmit={() => setTurnStart(chat.messages.length)}
              className="flex flex-col rounded-2xl border border-[var(--line)] bg-[var(--paper)] px-4 py-3 transition focus-within:border-[var(--accent)]"
            >
              <ComposerPrimitive.Input
                rows={1}
                placeholder="What needs my attention?"
                className="min-w-0 flex-1 resize-none border-0 bg-transparent text-[15px] outline-none placeholder:text-[var(--ink-faint)]"
              />
              <div className="mt-2 flex items-center justify-between">
                <span
                  aria-hidden="true"
                  className="text-lg leading-none text-[var(--ink-faint)]"
                >
                  +
                </span>
                {busy ? (
                  <ComposerPrimitive.Cancel className="rounded-full border border-[var(--line)] bg-[var(--paper)] px-3.5 py-1.5 text-[13px] font-medium text-[var(--ink-soft)]">
                    Stop
                  </ComposerPrimitive.Cancel>
                ) : (
                  <ComposerPrimitive.Send
                    aria-label="Ask"
                    className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--accent)] text-white transition hover:brightness-110 disabled:bg-[var(--wash)] disabled:text-[var(--ink-faint)]"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="18"
                      height="18"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 19V5" />
                      <path d="m5 12 7-7 7 7" />
                    </svg>
                  </ComposerPrimitive.Send>
                )}
              </div>
            </ComposerPrimitive.Root>
          </div>
        </ThreadPrimitive.Root>
      </LandingActionsContext.Provider>
    </AssistantRuntimeProvider>
  );
}

/**
 * Office Agent chat. This is the assistant-ui thread on the Agent tab.
 * A docked sidebar, if we add one later, should reuse this runtime rather
 * than a second hand-rolled message list.
 */
export default function DispatchOperator({
  authUserId,
  timezone,
  proposals,
  proposalBusy,
  onNavigate,
  onProposalCreated,
  onApprove,
  onReject,
  userName = "",
}: {
  authUserId: string;
  timezone: string;
  proposals: DispatchProposal[];
  proposalBusy: string | null;
  onNavigate: (navigation: OperatorNavigation) => void;
  onProposalCreated: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  userName?: string;
}) {
  const processed = useRef(new Set<string>());
  const restoredHistory = useRef(false);
  const agent = useAgent<DispatchOperatorState>({
    agent: "DispatchOperatorAgent",
    name: `dispatch-${authUserId}`,
    host: API_URL,
  });
  const chat = useAgentChat({
    agent,
    credentials: "include",
    autoContinueAfterToolResult: true,
    resume: true,
  });
  const busy =
    chat.isStreaming ||
    chat.isRecovering ||
    chat.isToolContinuation ||
    (chat.status as string) === "submitted";

  useEffect(() => {
    if (!restoredHistory.current) {
      if (chat.messages.length === 0) return;
      for (const message of chat.messages)
        for (const item of outputs(message)) processed.current.add(item.key);
      restoredHistory.current = true;
      return;
    }
    for (const message of chat.messages) {
      for (const item of outputs(message)) {
        if (processed.current.has(item.key)) continue;
        processed.current.add(item.key);
        if (item.output.kind === "navigation") onNavigate(item.output);
        if (item.output.kind === "proposal") onProposalCreated();
      }
    }
  }, [chat.messages, onNavigate, onProposalCreated]);

  return (
    <div className="relative w-full max-w-5xl">
      <AssistantLanding
        chat={chat}
        busy={busy}
        error={Boolean(chat.connectionError)}
        userName={userName}
        actions={{
          timezone,
          proposals,
          proposalBusy,
          onNavigate,
          onApprove,
          onReject,
        }}
      />
    </div>
  );
}
